// @flow weak
/** @jsx jsx */

import _ from 'lodash';
import { Fragment } from 'react';

import { Colors } from '@zapier/theme';
import { css, jsx } from '@emotion/core';
import { Icon } from '@zapier/common-components';

import FieldMessage from 'app/developer-v3/components/FieldMessage';
import HelpBox from 'app/developer-v3/components/HelpBox';

import type { Node } from 'react';

const createHelpBox = (help: {
  heading: string,
  description: string | Node,
}) => (
  <HelpBox
    backgroundColor="warning10"
    borderColor="warning"
    css={css({ marginTop: 20 })}
    description={<span>{help.description}</span>}
    heading={
      <span css={css({ display: 'flex', alignItems: 'center' })}>
        <Icon color="wolverine" name="alert" size={22} />
        &nbsp;
        {help.heading}
      </span>
    }
  />
);

const plugin = config => {
  const {
    createElement_Field: origCreateElement_Field,
    renderTag: origRenderTag,
  } = config;

  config.fieldMessages = {
    createHelpBox,
  };

  return {
    /**
     * Appends the field message below fields children. ex:
     * <Field>
     *   <Input />
     *   message && <FieldMessage />
     * </Field>
     **/

    createElement_Field(props, children) {
      const fieldErrors = props.field.meta.errors;
      const fieldWarnings = props.field.meta.warnings;
      const fieldInfo = props.field.meta.info;

      if (!fieldErrors && !fieldWarnings && !fieldInfo) {
        return origCreateElement_Field(props, children);
      }

      const errorMessage = fieldErrors && fieldErrors[props.field.key];
      const warningMessage = fieldWarnings && fieldWarnings[props.field.key];
      const fieldInfoMessage = fieldInfo && fieldInfo[props.field.key]?.message;
      const infoType = fieldInfo && fieldInfo[props.field.key]?.type;
      const fieldMessage = errorMessage || warningMessage;

      const newChildren = (
        <Fragment>
          {children}
          {fieldMessage && (
            <FieldMessage
              fieldKey={_.get(props, ['field', 'key'], '')}
              help={fieldMessage.help}
              message={fieldMessage}
              type={errorMessage ? 'error' : 'warning'}
            />
          )}
          {fieldInfoMessage && (
            <FieldMessage
              fieldKey={_.get(props, ['field', 'key'], '')}
              message={fieldInfoMessage}
              type={_.camelCase(`info ${infoType}`)}
            />
          )}
        </Fragment>
      );

      return origCreateElement_Field(props, newChildren);
    },

    // Style danger and info borders on input elements
    renderTag(Tag, tagProps, metaProps, ...children) {
      const newTagProps = { ...tagProps };
      const { typeName, elementName } = metaProps;
      const hasError = _.get(
        metaProps.field,
        ['meta', 'errors', metaProps.field.key],
        false
      );
      const infoType = _.get(
        metaProps.field,
        ['meta', 'info', metaProps.field.key, 'type'],
        false
      );

      if (Tag === 'textarea' && metaProps.field?.placeholder) {
        newTagProps.placeholder = metaProps.field.placeholder;
      }

      if (hasError || infoType) {
        const isPrettySelectElement =
          typeName === 'PrettySelect' &&
          (elementName === 'SelectArrow' ||
            elementName === 'PrettyTextInputTabTarget');
        const isStringInputTag = Tag === 'input' || Tag === 'textarea';

        if (isStringInputTag || isPrettySelectElement) {
          const infoBorderColor = {
            errors: Colors.lava,
            warnings: Colors.heroine,
            activationTasks: Colors.heroine,
          };

          newTagProps.style = {
            borderColor: hasError
              ? Colors.lava
              : infoType
              ? infoBorderColor[infoType]
              : '',
          };
        }
      }

      return origRenderTag(Tag, newTagProps, metaProps, ...children);
    },
  };
};

export default plugin;
